// src/components/student/WeeklyDriverAssignment.jsx
//
// Admin screen: lists every active pickup student with one dropdown per
// registered pickup weekday, so a student can have a different driver
// (or staff) on different days without a manual weekly re-assignment.
//
// Data model: adds a `driverSchedule` map field on the student doc, e.g.
//   driverSchedule: { Monday: 'driver1', Wednesday: 'staff', Thursday: 'driver2' }
// Falls back to the existing `assignedDriver` field wherever a day isn't
// set yet, so existing students keep working with no migration needed.
//
// Wire this in the same way AllStudents/AddStudent are wired in App.js:
//   import WeeklyDriverAssignment from './components/student/WeeklyDriverAssignment';
//   if (currentView === 'driver-schedule') {
//     return <WeeklyDriverAssignment onBack={() => setCurrentView('all-students')} />;
//   }
// and add a button somewhere in the admin nav that does setCurrentView('driver-schedule').

import React, { useEffect, useState } from 'react';
import { db } from '../../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useStudentContext } from '../../contexts/StudentContext';
import { getAllDrivers } from '../../services/driverService';
import { styles } from '../../styles/theme';

// Add/edit entries here to match who can actually be picked a driver.
// 'staff' is a plain non-driver option for in-house pickup.

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const parsePickupDays = (pickupDays) => {
  if (!pickupDays) return [];
  if (Array.isArray(pickupDays)) return pickupDays.map(d => d.trim());
  return pickupDays.split(/[\s,]+/).filter(Boolean);
};

const WeeklyDriverAssignment = ({ onBack }) => {
  const { getActiveStudents } = useStudentContext();
  const students = getActiveStudents()
    .filter(s => s.needsPickup && s.programStatus !== 'inactive');
  const [savingKey, setSavingKey] = useState(null); // `${studentId}-${day}` while a cell is saving
  const [drivers, setDrivers] = useState([]);
const [loadingDrivers, setLoadingDrivers] = useState(true);


useEffect(() => {
  const loadDrivers = async () => {
    try {
      const data = await getAllDrivers();
      setDrivers(data);
    } catch (error) {
      console.error('Error loading drivers:', error);
      setDrivers([]);
    } finally {
      setLoadingDrivers(false);
    }
  };
  loadDrivers();
}, []);

  const handleAssign = async (studentId, day, driverId) => {
    const key = `${studentId}-${day}`;
    setSavingKey(key);
    try {
      const studentRef = doc(db, 'students', studentId);
      await updateDoc(studentRef, {
        [`driverSchedule.${day}`]: driverId
      });
    } catch (error) {
      console.error('Error updating driver schedule:', error);
      alert('Failed to update driver assignment for that day.');
    } finally {
      setSavingKey(null);
    }
  };

  const getDayValue = (student, day) => {
  return (student.driverSchedule && student.driverSchedule[day]) || student.assignedDriver || '';
};

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <div style={styles.header}>
        <div style={styles.centerContainer}>
          <button style={styles.backButton} onClick={onBack}>
            ← Back
          </button>
          <h1 style={styles.headerTitle}>Weekly Driver Assignment</h1>
          <p style={styles.headerSubtitle}>
            Set who picks up each student on each day - {students.length} pickup students
          </p>
        </div>
      </div>

      <div style={styles.centerContainer}>
        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                    Student
                  </th>
                  {WEEKDAYS.map(day => (
                    <th key={day} style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                      {day.slice(0, 3)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => {
                  const registeredDays = parsePickupDays(student.pickupDays);
                  return (
                    <tr
                      key={student.id}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                      }}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                          {student.firstName} {student.lastName}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {student.schoolName || '-'}
                        </div>
                      </td>
                      {WEEKDAYS.map(day => {
                        const isRegistered = registeredDays.some(
                          d => d.toLowerCase() === day.toLowerCase()
                        );
                        const key = `${student.id}-${day}`;
                        return (
                          <td key={day} style={{ padding: '0.5rem', textAlign: 'center' }}>
                            {isRegistered ? (
                              <select
                                value={getDayValue(student, day)}
                                onChange={(e) => handleAssign(student.id, day, e.target.value)}
                                disabled={savingKey === key || loadingDrivers}
                                style={{
                                  ...styles.select,
                                  marginBottom: 0,
                                  padding: '0.4rem',
                                  fontSize: '0.75rem',
                                  cursor: savingKey === key ? 'wait' : 'pointer',
                                  opacity: savingKey === key ? 0.6 : 1
                                }}
                              >
                                <option value="">-- Select --</option>
{drivers.map(driver => (
  <option key={driver.id} value={driver.id}>
    {driver.name} {driver.status !== 'active' ? `(${driver.status})` : ''}
  </option>
))}
                              </select>
                            ) : (
                              <span style={{ color: '#d1d5db' }}>-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {students.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: '#6b7280' }}>
                No active pickup students found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyDriverAssignment;
